// useSideCoverageCheck — coordinates coverage verification for ALL image layers on one side.
//
// Problem with per-layer check:
//   Each ImageLayer called checkArtworkCoverage independently → the last one to resolve
//   overwrote the result for all previous ones, ignoring that multiple artworks collectively
//   cover the boundary line (e.g. layer A covers top, layer B covers bottom → both valid).
//
// Solution:
//   This hook acts as a side-level coordinator.
//   Each ImageLayer registers (layerId, image HTMLElement, placement attrs).
//   When ANY layer changes (upload / drag / transform), the hook composites ALL registered
//   artworks onto a single OffscreenCanvas in template space, then sends that composite
//   directly to the OpenCV worker (bypassing L1 rect check — composite is already in
//   template space so bounding-box check is meaningless).
//
// API:
//   const { requestCheck } = useSideCoverageCheck({ background, selectedSide })
//   - requestCheck(layerId, image, imageProps, offscreenCanvas)
//       → debounced; updates layer snapshot then composites+checks all layers

import {useCallback, useEffect, useRef} from 'react'
import PubSub from '../../Broker'
import {checkCoverageWithWorker} from '../../../../webworkers/helpers/coverage-checker'
import {useSelectedVariantContext} from '../../context/SelectedVariantContext'

const DEBOUNCE_MS = 1000
const TAG = '[SideCoverage]'

export function useSideCoverageCheck({background, selectedSide}) {
    const {state: {currentBgImage, applyBgAllSide, selectedAttribute, product}} = useSelectedVariantContext()
    const currentSideBg = selectedAttribute?.safeZone?.[selectedSide]

    // Only v1/v2/v3 mockup types have a grey-zone template
    // Prefer mockup_type, fallback to mockups[0].meta.version (some store APIs omit mockup_type)
    const mockupVersion = product?.mockup_type || product?.mockups?.[0]?.meta?.version
    const shouldVerifyCoverage = ['v1', 'v2', 'v3'].includes(mockupVersion)

    // Map: layerId → { image: HTMLImageElement, imageProps: object }
    const layersRef = useRef(new Map())
    const timerRef = useRef(null)

    // Clear stale layers when switching sides — they belong to the previous side
    const prevSideRef = useRef(selectedSide)
    if (prevSideRef.current !== selectedSide) {
        layersRef.current.clear()
        prevSideRef.current = selectedSide
    }

    const _runCheck = useCallback(async () => {
        const W = background?.width
        const H = background?.height

        if (!shouldVerifyCoverage) {
            console.log(`${TAG} [${selectedSide}] skip — shouldVerifyCoverage=false (mockupVersion=${mockupVersion})`)
            return
        }
        if (!W || !H) {
            console.log(`${TAG} [${selectedSide}] skip — background not ready (${W}x${H})`)
            return
        }

        // Skip when background fully covers product
        if (currentBgImage?.file || applyBgAllSide || currentSideBg?.isUserSetFill) {
            console.log(`${TAG} [${selectedSide}] skip — BG active (bgFile=${!!currentBgImage?.file} applyAll=${applyBgAllSide} isUserSetFill=${!!currentSideBg?.isUserSetFill})`)
            PubSub.publish('ARTWORK_COVERAGE_CHECK', [{side: selectedSide, result: {ok: true, coveragePercent: 100, uncoveredEdges: [], uncoveredCorners: []}}])
            return
        }

        const allLayers = layersRef.current
        const readyLayers = [...allLayers.values()].filter(e => e.image && e.imageProps)

        console.log(`${TAG} [${selectedSide}] START check | total registered=${allLayers.size} ready=${readyLayers.length} | template=${W}x${H}`)

        if (readyLayers.length === 0) {
            console.log(`${TAG} [${selectedSide}] skip — no ready layers, clearing warning`)
            PubSub.publish('ARTWORK_COVERAGE_CHECK', [{side: selectedSide, result: {ok: true, coveragePercent: 100, uncoveredEdges: [], uncoveredCorners: []}}])
            return
        }

        const bgX = background.x || 0
        const bgY = background.y || 0

        // ── Step 1: Composite all artwork layers onto one OffscreenCanvas ──────
        // Size = template dimensions. Each layer drawn at its Konva transform position.
        // Replicates Konva Rect transform:
        //   ctx.translate(pivot)  →  ctx.rotate(deg)  →  ctx.drawImage(-offsetX, -offsetY, W, H)
        let compositeCanvas
        try {
            compositeCanvas = new OffscreenCanvas(W, H)
        } catch (err) {
            console.error(`${TAG} [${selectedSide}] OffscreenCanvas not supported — abort`, err)
            return
        }

        const ctx = compositeCanvas.getContext('2d')
        ctx.clearRect(0, 0, W, H)

        readyLayers.forEach((entry, idx) => {
            const p = entry.imageProps
            const scaleX = Math.abs(p.scaleX || 1)
            const scaleY = Math.abs(p.scaleY || 1)
            const offsetX = (p.offsetX || 0) * scaleX
            const offsetY = (p.offsetY || 0) * scaleY
            const drawW = p.width * scaleX
            const drawH = p.height * scaleY
            // Subtract background origin to convert from Stage space → template space
            const pivotX = p.x - bgX
            const pivotY = p.y - bgY
            const rotation = p.rotation || 0

            console.log(
                `${TAG} [${selectedSide}] Layer ${idx + 1}/${readyLayers.length}:`,
                `pivot=(${pivotX.toFixed(1)},${pivotY.toFixed(1)})`,
                `offset=(${offsetX.toFixed(1)},${offsetY.toFixed(1)})`,
                `size=(${drawW.toFixed(1)}x${drawH.toFixed(1)})`,
                `rot=${rotation.toFixed(1)}°`,
                `src=${entry.image.src?.slice(-40)}`
            )

            ctx.save()
            ctx.translate(pivotX, pivotY)
            if (rotation) ctx.rotate(rotation * Math.PI / 180)
            ctx.drawImage(entry.image, -offsetX, -offsetY, drawW, drawH)
            ctx.restore()
        })

        console.log(`${TAG} [${selectedSide}] Composite done → ${W}x${H}px canvas`)

        // ── Step 2: Build safezone ─────────────────────────────────────────────
        const safezoneRect = background.safezoneRect || null
        const safezone = safezoneRect
            ? {x: safezoneRect.x, y: safezoneRect.y, width: safezoneRect.width, height: safezoneRect.height}
            : {x: 0, y: 0, width: W, height: H}
        const templateSize = {width: W, height: H}
        const maskSrc = background.safezoneImagePath || background.src

        console.log(`${TAG} [${selectedSide}] safezone=`, safezone, `maskSrc=${maskSrc?.slice(-40)}`)

        // ── Step 3: Check transparency of composite ────────────────────────────
        // Composite canvas is an OffscreenCanvas — hasTransparency uses document.createElement
        // which can't drawImage from OffscreenCanvas in all browsers. Use a dedicated check.
        const isTransparent = _hasTransparencyOffscreen(compositeCanvas, W, H)
        console.log(`${TAG} [${selectedSide}] composite hasTransparency=${isTransparent}`)

        if (!isTransparent || !maskSrc) {
            // Opaque composite fully covers template → ok
            console.log(`${TAG} [${selectedSide}] opaque composite or no maskSrc → result ok=true`)
            PubSub.publish('ARTWORK_COVERAGE_CHECK', [{side: selectedSide, result: {ok: true, coveragePercent: 100, uncoveredEdges: [], uncoveredCorners: []}}])
            return
        }

        // ── Step 4: Transfer composite bitmap to worker ────────────────────────
        // The composite is already in template space. Placement = identity (full canvas).
        // Worker will draw artBitmap at placement coords — since we pass a full-template-size
        // bitmap with identity placement, it gets drawn 1:1 → correct coverage check.
        const placement = {
            pivotX: 0,
            pivotY: 0,
            offsetX: 0,
            offsetY: 0,
            width: W,
            height: H,
            rotation: 0,
            x: 0,
            y: 0,
        }

        let artBitmap
        try {
            artBitmap = compositeCanvas.transferToImageBitmap()
            console.log(`${TAG} [${selectedSide}] transferToImageBitmap OK — sending to worker`)
        } catch (err) {
            console.error(`${TAG} [${selectedSide}] transferToImageBitmap failed`, err)
            return
        }

        try {
            const t0 = performance.now()
            const result = await checkCoverageWithWorker({
                artBitmap,
                templateSrc: maskSrc,
                placement,
                templateSize,
                safezoneRect: safezone,
            })
            const elapsed = (performance.now() - t0).toFixed(1)
            if (result === null) {
                console.log(`${TAG} [${selectedSide}] worker result superseded (null) after ${elapsed}ms`)
                return
            }
            console.log(
                `${TAG} [${selectedSide}] worker result after ${elapsed}ms:`,
                `ok=${result.ok}`,
                `coverage=${result.coveragePercent}%`,
                `uncoveredEdges=`, result.uncoveredEdges,
                `uncoveredCorners=`, result.uncoveredCorners
            )
            PubSub.publish('ARTWORK_COVERAGE_CHECK', [{side: selectedSide, result}])
        } catch (err) {
            console.error(`${TAG} [${selectedSide}] worker check failed:`, err)
        }
    }, [
        background,
        selectedSide,
        mockupVersion,
        shouldVerifyCoverage,
        currentBgImage?.file,
        applyBgAllSide,
        currentSideBg?.isUserSetFill,
    ])

    const _scheduleCheck = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(_runCheck, DEBOUNCE_MS)
    }, [_runCheck])

    // ── Public API ─────────────────────────────────────────────────────────────

    /**
     * Request a debounced coverage check.
     * Updates the layer snapshot then schedules composite+check.
     * Called from ImageLayer on: upload complete, dragMove, dragEnd, transformEnd.
     */
    const requestCheck = useCallback((layerId, image, imageProps) => {
        layersRef.current.set(layerId, {image, imageProps})
        console.log(`${TAG} [${selectedSide}] requestCheck layerId=${layerId} | map size=${layersRef.current.size}`)
        _scheduleCheck()
    }, [_scheduleCheck, selectedSide])

    /**
     * Unregister a layer on ImageLayer unmount.
     * Re-runs check so remaining layers are re-evaluated.
     */
    const unregisterLayer = useCallback((layerId) => {
        layersRef.current.delete(layerId)
        console.log(`${TAG} [${selectedSide}] unregisterLayer ${layerId} | remaining=${layersRef.current.size}`)
        if (layersRef.current.size > 0) _scheduleCheck()
    }, [_scheduleCheck, selectedSide])

    // React to BG changes at side level
    useEffect(() => {
        if (!shouldVerifyCoverage) return
        if (currentBgImage?.file || applyBgAllSide) {
            PubSub.publish('ARTWORK_COVERAGE_BG_ACTIVE', null)
        } else if (currentSideBg?.isUserSetFill) {
            PubSub.publish('ARTWORK_COVERAGE_CHECK', [{side: selectedSide, result: {ok: true, coveragePercent: 100, uncoveredEdges: [], uncoveredCorners: []}}])
        } else {
            _scheduleCheck()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBgImage?.file, applyBgAllSide, currentSideBg?.isUserSetFill, shouldVerifyCoverage])

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [])

    return {requestCheck, unregisterLayer}
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

/**
 * Check if an OffscreenCanvas has any transparent pixels (alpha < 128).
 * Uses a 50×50 thumbnail drawn directly from the OffscreenCanvas.
 * Cannot use document.createElement in worker context or with OffscreenCanvas source.
 */
function _hasTransparencyOffscreen(offscreenCanvas, W, H) {
    try {
        const size = 50
        const thumb = new OffscreenCanvas(size, size)
        const ctx = thumb.getContext('2d')
        ctx.drawImage(offscreenCanvas, 0, 0, size, size)
        const data = ctx.getImageData(0, 0, size, size).data
        for (let i = 3; i < data.length; i += 4) {
            if (data[i] < 128) return true
        }
        return false
    } catch (_) {
        // Fallback: assume transparent (safer — go to worker)
        return true
    }
}

